import { getDefaultConfig } from '@rainbow-me/rainbowkit';
import {
  injectedWallet,
  rainbowWallet,
  walletConnectWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { metaMaskExtensionWallet } from './metaMaskExtensionWallet';
import { createPublicClient, defineChain, fallback, http } from 'viem';
import { createConfig } from 'wagmi';
import { injected, walletConnect } from 'wagmi/connectors';
import { SITE_ORIGIN } from '../config/siteUrl';
import { rpcHttpUrls } from './rpcEnv';

const chainId = Number(process.env.REACT_APP_CHAIN_ID);
const chainNameRaw = process.env.REACT_APP_CHAIN_NAME;
const chainName = typeof chainNameRaw === 'string' ? chainNameRaw.replace(/^"|"$/g, '') : 'Custom';
const explorerUrlRaw = process.env.REACT_APP_BLOCK_EXPLORER_URL;
const explorerUrl = typeof explorerUrlRaw === 'string' ? explorerUrlRaw.replace(/^"|"$/g, '') : undefined;
const nativeSymbolRaw = process.env.REACT_APP_NATIVE_TOKEN_SYMBOL;
const nativeSymbol = typeof nativeSymbolRaw === 'string' ? nativeSymbolRaw.replace(/^"|"$/g, '') : 'ETH';
/** MetaMask / RainbowKit require 18 for EIP-3085 `nativeCurrency.decimals`. */
const WALLET_CHAIN_NATIVE_DECIMALS = 18;
const walletConnectProjectIdRaw = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;
const walletConnectProjectId = typeof walletConnectProjectIdRaw === 'string'
  ? walletConnectProjectIdRaw.replace(/^"|"$/g, '').trim()
  : '';
const hasWalletConnectProjectId = Boolean(
  walletConnectProjectId && walletConnectProjectId !== 'YOUR_PROJECT_ID',
);

const publicUrlRaw = process.env.REACT_APP_PUBLIC_URL;
const publicUrlFromEnv =
  typeof publicUrlRaw === 'string' ? publicUrlRaw.replace(/^"|"$/g, '').trim() : '';

/** Slow block polling — Arc free RPCs cannot handle wagmi's default ~4s poll + fallback ranking. */
const POLLING_INTERVAL_MS = 25_000;

/** Origin used in WalletConnect metadata (important for mobile deep links). */
export function resolveWalletAppOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  if (publicUrlFromEnv) {
    return publicUrlFromEnv.replace(/\/$/, '');
  }
  return SITE_ORIGIN;
}

function createArcTransport() {
  // No retries on 429 — retries multiply console spam and burn quota.
  const httpOpts = { timeout: 25_000, retryCount: 0 };
  if (rpcHttpUrls.length === 0) {
    return http(undefined, httpOpts);
  }
  if (rpcHttpUrls.length === 1) {
    return http(rpcHttpUrls[0], httpOpts);
  }
  // rank:false — do NOT continuously re-probe every fallback (that caused the 429 loop).
  return fallback(
    rpcHttpUrls.map((url) => http(url, httpOpts)),
    { rank: false },
  );
}

const arcTransport = createArcTransport();

export const chain = defineChain({
  id: chainId,
  name: chainName,
  nativeCurrency: {
    name: nativeSymbol,
    symbol: nativeSymbol,
    decimals: WALLET_CHAIN_NATIVE_DECIMALS,
  },
  rpcUrls: {
    default: { http: rpcHttpUrls.length ? rpcHttpUrls : [] },
    public: { http: rpcHttpUrls.length ? rpcHttpUrls : [] },
  },
  blockExplorers: explorerUrl
    ? {
        default: {
          name: 'Explorer',
          url: explorerUrl,
        },
      }
    : undefined,
  testnet: chainId === 5042002,
});

export const publicClient = createPublicClient({
  chain,
  transport: arcTransport,
  pollingInterval: POLLING_INTERVAL_MS,
});

export { primaryRpcUrl, rpcHttpUrls } from './rpcEnv';

export const blockExplorerUrl = explorerUrl ? explorerUrl.replace(/\/+$/, '') : '';

function createLegacyConfig() {
  if (!hasWalletConnectProjectId) {
    // eslint-disable-next-line no-console
    console.warn(
      '[CronPay] Set REACT_APP_WALLETCONNECT_PROJECT_ID for mobile wallet apps (Rainbow, MetaMask, etc.).',
    );
  }

  const appOrigin = resolveWalletAppOrigin();
  const metadata = appOrigin
    ? {
        name: 'CronPay',
        description: 'On-chain payments and job escrow on Arc',
        url: appOrigin,
        icons: [`${appOrigin}/logo192.png`],
      }
    : undefined;

  return createConfig({
    chains: [chain],
    connectors: [
      injected(),
      ...(hasWalletConnectProjectId
        ? [
            walletConnect({
              projectId: walletConnectProjectId,
              metadata,
              showQrModal: true,
            }),
          ]
        : []),
    ],
    transports: {
      [chain.id]: arcTransport,
    },
    pollingInterval: POLLING_INTERVAL_MS,
  });
}

function createRainbowKitConfig() {
  const appOrigin = resolveWalletAppOrigin();
  const appIcon = appOrigin ? `${appOrigin}/logo192.png` : undefined;

  return getDefaultConfig({
    appName: 'CronPay',
    appDescription: 'On-chain payments and job escrow on Arc',
    appUrl: appOrigin,
    appIcon,
    projectId: walletConnectProjectId,
    chains: [chain],
    transports: {
      [chain.id]: arcTransport,
    },
    pollingInterval: POLLING_INTERVAL_MS,
    wallets: [
      {
        groupName: 'Recommended',
        wallets: [
          metaMaskExtensionWallet,
          injectedWallet,
          rainbowWallet,
          walletConnectWallet,
        ],
      },
    ],
  });
}

export const config = hasWalletConnectProjectId
  ? createRainbowKitConfig()
  : createLegacyConfig();
